import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useCatalogStore } from './catalog';
import { useJournalStore } from './journal';
import { emitEvent } from '@/utils/eventBus';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 100;
const COS_CATALOG_NAME = 'COS';

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		name: attributes.name ?? data.name ?? '',
	};
};

const normalizePurchase = (entry = {}) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		date: attributes.date ?? null,
		net: attributes.net ?? null,
		vat: attributes.vat ?? null,
		gross: attributes.gross ?? null,
		notes: attributes.notes ?? '',
		documentStatus: attributes.documentStatus ?? '',
		vendor: normalizeRelation(attributes.vendor),
		cos: normalizeRelation(attributes.cos),
		building: normalizeRelation(attributes.building),
		journal: normalizeRelation(attributes.journal),
	};
};

// Reads one page, or every page, of the journal's purchases; the caller decides what to keep.
// Returns null when isCurrent() says a newer request has taken over.
const requestPurchases = async (store, options = {}, isCurrent = () => true) => {
	const allPages = !!options.allPages;
	const resolvedPageSize = options.pageSize ?? store.pagination.pageSize;
	const fetchPage = async (page) =>
		axios.get(`${STRAPI_URL}/api/journal-purchases-items`, {
			headers: store.authHeaders(),
			params: store.buildQueryParams({
				...options,
				page,
				pageSize: resolvedPageSize,
			}),
		});

	const firstPage = allPages ? 1 : (options.page ?? store.pagination.page ?? 1);
	const firstResponse = await fetchPage(firstPage);
	if (!isCurrent()) return null;

	const firstEntries = firstResponse?.data?.data || [];
	const metaPagination = firstResponse?.data?.meta?.pagination || {};
	let allEntries = [...firstEntries];

	const pageCount = Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
		? metaPagination.pageCount
		: 1;

	if (allPages && pageCount > 1) {
		for (let page = 2; page <= pageCount; page += 1) {
			if (!isCurrent()) return null;
			// eslint-disable-next-line no-await-in-loop
			const response = await fetchPage(page);
			if (!isCurrent()) return null;
			allEntries = allEntries.concat(response?.data?.data || []);
		}
	}

	const total = allPages
		? allEntries.length
		: (Number.isFinite(metaPagination.total) ? metaPagination.total : store.pagination.total);
	const pageSize = allPages
		? (total || resolvedPageSize)
		: (Number.isFinite(metaPagination.pageSize)
			? metaPagination.pageSize
			: store.pagination.pageSize);
	const page = allPages
		? 1
		: (Number.isFinite(metaPagination.page) ? metaPagination.page : store.pagination.page);
	const nextPageCount = allPages
		? 1
		: (Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
			? metaPagination.pageCount
			: Math.max(1, Math.ceil(total / pageSize)));

	return {
		items: allEntries.map(normalizePurchase),
		pagination: {
			page,
			pageSize,
			total,
			pageCount: nextPageCount,
		},
	};
};

const normalizeIsoDate = (value) => {
	if (!value) return null;
	const str = String(value);
	return str.length >= 10 ? str.slice(0, 10) : str;
};

const toUniqueStrings = (values = []) =>
	[...new Set((values || []).map((v) => (v === null || v === undefined ? '' : String(v))).filter(Boolean))];

export const useJournalPurchasesStore = defineStore('journalPurchases', {
	state: () => ({
		items: [],
		isLoading: false,
		error: null,
		initialized: false,
		pagination: {
			page: 1,
			pageSize: DEFAULT_PAGE_SIZE,
			total: 0,
			pageCount: 1,
		},
		filters: {
			vendorId: null,
			buildingId: null,
			documentStatus: '',
			dateFrom: null,
			dateTo: null,
			search: '',
		},
	}),
	getters: {
		vendorOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.vendorOptions || catalogStore.optionsByCatalogName?.('Vendors') || [];
		},
		buildingOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.buildingOptions;
		},
		cosOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.optionsByCatalogName?.(COS_CATALOG_NAME) || [];
		},
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		purchasesJournalId() {
			const journalStore = useJournalStore();
			const match = (journalStore.journalList || []).find(
				(entry) => (entry.name || '').trim().toLowerCase() === 'purchases',
			);
			if (!match || match.isVirtual) return null;
			return match.id || null;
		},

		applyJournalScope(params = {}) {
			const journalId = this.purchasesJournalId();
			if (journalId) {
				params['filters[journal][id][$eq]'] = journalId;
			}
			return params;
		},

		normalizeDateRange(from, to) {
			const parse = (value) => {
				if (!value) return null;
				const date = new Date(value);
				return Number.isNaN(date.getTime()) ? null : { date, raw: value };
			};

			let start = parse(from);
			let end = parse(to);

			if (start && end && start.date > end.date) {
				[start, end] = [end, start];
			}
			if (start && !end) {
				end = start;
			} else if (!start && end) {
				start = end;
			}

			return {
				from: start?.raw || '',
				to: end?.raw || '',
			};
		},

			hasActiveDateRange() {
				const { from, to } = this.normalizeDateRange(this.filters.dateFrom, this.filters.dateTo);
				return !!from || !!to;
			},

		buildQueryParams(overrides = {}) {
			const page = overrides.page ?? this.pagination.page;
			const pageSize = overrides.pageSize ?? this.pagination.pageSize;
			const params = {
				'pagination[page]': page,
				'pagination[pageSize]': pageSize,
				'sort[0]': 'date:desc',
				'sort[1]': 'id:desc',
				'populate[vendor][fields][0]': 'name',
				'populate[cos][fields][0]': 'name',
				'populate[building][fields][0]': 'name',
				'populate[journal][fields][0]': 'name',
			};

			const { filters } = this;
			if (filters.vendorId) {
				params['filters[vendor][id][$eq]'] = filters.vendorId;
			}
			if (filters.buildingId) {
				params['filters[building][id][$eq]'] = filters.buildingId;
			}
			if (filters.documentStatus) {
				params['filters[documentStatus][$eqi]'] = filters.documentStatus;
			}
			const { from: normalizedFrom, to: normalizedTo } = this.normalizeDateRange(
				filters.dateFrom,
				filters.dateTo,
			);
			if (normalizedFrom) {
				params['filters[date][$gte]'] = normalizedFrom;
			}
			if (normalizedTo) {
				params['filters[date][$lte]'] = normalizedTo;
			}
			if (filters.search?.trim()) {
				params['filters[$or][0][notes][$containsi]'] = filters.search.trim();
			}
			return this.applyJournalScope(params);
			},

			async fetchPurchases(options = {}) {
				this._activeFetchToken ??= null;
				const requestToken = Symbol('fetchPurchases');
				this._activeFetchToken = requestToken;
				this.isLoading = true;
				this.error = null;
				try {
					const result = await requestPurchases(this, options, () => this._activeFetchToken === requestToken);
					if (!result) return;
					this.items = result.items;
					this.pagination = result.pagination;
			} catch (error) {
				if (this._activeFetchToken === requestToken) {
					this.error =
						error?.response?.data?.error?.message ||
						error.message ||
						'Failed to load purchases';
				}
				throw error;
			} finally {
				if (this._activeFetchToken === requestToken) {
					this.isLoading = false;
					this._activeFetchToken = null;
				}
			}
			},

			// Reads the same page again without touching the list, so the page can decide
			// whether it is safe to show what changed elsewhere.
			async peekPurchases(options = {}) {
				return requestPurchases(this, options);
			},

			async updateFilters(partial = {}) {
				this.filters = { ...this.filters, ...partial };
				this.pagination = { ...this.pagination, page: 1 };
				const allPages = this.hasActiveDateRange();
				await this.fetchPurchases({ page: 1, allPages });
			},

				async changePage(page) {
					if (this.hasActiveDateRange()) {
						return;
					}
					const safePageCount = Number.isFinite(this.pagination.pageCount) && this.pagination.pageCount > 0
						? this.pagination.pageCount
						: 1;
					const targetPage = Math.max(1, Math.min(page, safePageCount));
			if (targetPage === this.pagination.page) {
				return;
			}
			await this.fetchPurchases({ page: targetPage });
			},

			async refresh() {
				await this.fetchPurchases({ allPages: this.hasActiveDateRange() });
			},

	async queryPurchasesInRange({
		startDate,
		endDate,
		buildingId,
		buildingIds = [],
		pageSize = 100,
	} = {}) {
		const headers = this.authHeaders();
		const safePageSize = Math.min(
			100,
			Math.max(1, Number.isFinite(Number(pageSize)) ? Number(pageSize) : 100),
		);
		const baseParams = {
			// Strapi maxLimit is configured to 100 on this project.
			'pagination[pageSize]': safePageSize,
			'sort[0]': 'date:asc',
			'sort[1]': 'id:asc',
			'populate[vendor][fields][0]': 'name',
			'populate[cos][fields][0]': 'name',
			'populate[building][fields][0]': 'name',
			'populate[journal][fields][0]': 'name',
		};

		if (startDate) {
			baseParams['filters[date][$gte]'] = startDate;
		}
		if (endDate) {
			baseParams['filters[date][$lte]'] = endDate;
		}
			if (Array.isArray(buildingIds) && buildingIds.length) {
				baseParams['filters[building][id][$in]'] = buildingIds;
			} else if (buildingId) {
				baseParams['filters[building][id][$eq]'] = buildingId;
			}
			this.applyJournalScope(baseParams);

		const collected = [];
		let page = 1;
		let pageCount = 1;

		do {
			const response = await axios.get(`${STRAPI_URL}/api/journal-purchases-items`, {
				headers,
				params: {
					...baseParams,
					'pagination[page]': page,
				},
			});

			const entries = response?.data?.data || [];
			collected.push(...entries);

			const pagination = response?.data?.meta?.pagination;
			pageCount = pagination?.pageCount ?? page;
			page += 1;
		} while (page <= pageCount);

		return collected.map(normalizePurchase);
	},

	async initialize({ forceCatalogs = false, forceData = false } = {}) {
		const catalogStore = useCatalogStore();
		const journalStore = useJournalStore();
		if (forceCatalogs || !catalogStore.initialized) {
			await catalogStore.bootstrap?.();
		}
		if (!journalStore.initialized) {
			await journalStore.initialize?.();
		}

		if (!this.initialized) {
			await this.fetchPurchases();
			this.initialized = true;
			return;
			}

			if (forceData) {
				await this.refresh();
			}
		},

	async createPurchase({ date, journalId, vendorId, cosId, buildingId } = {}) {
		if (!date) {
			throw new Error('Missing date for purchase creation');
		}
		const resolvedJournalId = journalId || this.purchasesJournalId();
		const payload = {
			date,
		};
		if (resolvedJournalId) {
			payload.journal = resolvedJournalId;
		}
		if (vendorId) {
			payload.vendor = vendorId;
		}
		if (cosId) {
			payload.cos = cosId;
		}
		if (buildingId) {
				payload.building = buildingId;
			}
			const response = await axios.post(
				`${STRAPI_URL}/api/journal-purchases-items`,
				{ data: payload },
				{
					headers: this.authHeaders(),
					params: {
						'populate[vendor][fields][0]': 'name',
						'populate[cos][fields][0]': 'name',
						'populate[building][fields][0]': 'name',
						'populate[journal][fields][0]': 'name',
					},
				}
			);
			const created = normalizePurchase(response?.data?.data || {});
			const normalizedDate = normalizeIsoDate(created.date);
			const normalizedBuildingId = created.building?.id || null;
			emitEvent('purchases:changed', {
				dates: normalizedDate ? [normalizedDate] : [],
				buildingId: normalizedBuildingId,
				buildingIds: normalizedBuildingId ? [String(normalizedBuildingId)] : [],
				purchaseIds: toUniqueStrings([created.id, created.documentId]),
			});
			return created;
		},

		async deletePurchase(documentId, purchase = null) {
			if (!documentId) return false;
			await axios.delete(
				`${STRAPI_URL}/api/journal-purchases-items/${documentId}`,
				{ headers: this.authHeaders() }
			);
			const normalizedPurchaseId = purchase?.id ?? null;
			const normalizedDocumentId = purchase?.documentId ?? documentId;
			const normalizedDate = normalizeIsoDate(purchase?.date);
			const normalizedBuildingId = purchase?.building?.id ?? purchase?.buildingId ?? null;
			emitEvent('purchases:changed', {
				dates: normalizedDate ? [normalizedDate] : [],
				buildingId: normalizedBuildingId,
				buildingIds: normalizedBuildingId ? [String(normalizedBuildingId)] : [],
				purchaseIds: toUniqueStrings([normalizedPurchaseId, normalizedDocumentId]),
			});
			return true;
		},

	async updatePurchase(documentId, payload, previousPurchase = null) {
		if (!documentId) throw new Error('Missing purchase identifier');
		const enforcedPayload = {
			...payload,
		};
		if (!enforcedPayload.journal) {
			const journalId = this.purchasesJournalId();
			if (journalId) {
				enforcedPayload.journal = journalId;
			}
		}
			const response = await axios.put(
				`${STRAPI_URL}/api/journal-purchases-items/${documentId}`,
				{ data: enforcedPayload },
				{
					headers: this.authHeaders(),
					params: {
						'populate[vendor][fields][0]': 'name',
						'populate[cos][fields][0]': 'name',
						'populate[building][fields][0]': 'name',
						'populate[journal][fields][0]': 'name',
					},
				}
			);
			const updated = normalizePurchase(response?.data?.data || {});
			const previous =
				previousPurchase ||
				this.items.find((item) => item.documentId === updated.documentId || item.id === updated.id) ||
				null;
			const previousDate = normalizeIsoDate(previous?.date);
			const nextDate = normalizeIsoDate(updated.date);
			const previousBuildingId = previous?.building?.id ?? previous?.buildingId ?? null;
			const nextBuildingId = updated.building?.id || null;
			const purchaseIds = toUniqueStrings([
				previous?.id,
				previous?.documentId,
				documentId,
				updated.id,
				updated.documentId,
			]);
			emitEvent('purchases:changed', {
				dates: toUniqueStrings([previousDate, nextDate]),
				buildingId: nextBuildingId,
				buildingIds: toUniqueStrings([previousBuildingId, nextBuildingId]),
				purchaseIds,
			});
			const idx = this.items.findIndex((item) => item.documentId === updated.documentId);
			if (idx !== -1) {
				const next = [...this.items];
				next[idx] = updated;
				this.items = next;
			}
			return updated;
		},
	},
});
